import React from 'react';

// Build a human-friendly activity timeline from current user fields and optional Ahoy events
// Props:
// - user: current user object (merged from store/props where possible)
// - ahoyEvents: optional array of recent ahoy events: [{name, time}] or raw events
// - maxItems: number of timeline items to display (default 5)
const YourActivity = ({ user = {}, ahoyEvents = [], maxItems = 5 }) => {
  const parseDate = (d) => {
    if (!d) return null;
    try { const t = new Date(d); return isNaN(t.getTime()) ? null : t; } catch (_) { return null; }
  };

  const toRelTime = (date) => {
    if (!date) return '';
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    const now = new Date();
    const diffSec = Math.round((date.getTime() - now.getTime()) / 1000);
    const absSec = Math.abs(diffSec);
    if (absSec < 60) return rtf.format(Math.round(diffSec), 'seconds');
    const diffMin = Math.round(diffSec / 60);
    if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minutes');
    const diffHr = Math.round(diffMin / 60);
    if (Math.abs(diffHr) < 24) return rtf.format(diffHr, 'hours');
    const diffDay = Math.round(diffHr / 24);
    if (Math.abs(diffDay) < 30) return rtf.format(diffDay, 'days');
    const diffMon = Math.round(diffDay / 30);
    if (Math.abs(diffMon) < 12) return rtf.format(diffMon, 'months');
    const diffYr = Math.round(diffMon / 12);
    return rtf.format(diffYr, 'years');
  };

  const labelForTriggeringEvent = (eventType) => {
    switch (eventType) {
      case 'wealth_gap': return 'Completed Wealth Gap';
      case 'profit_gap': return 'Completed Profit Gap';
      case 'owner_diagnostic': return 'Completed Owner Diagnostic';
      case 'value_gap': return 'Completed Value Gap';
      default: return `Completed ${String(eventType || 'activity').replace(/_/g, ' ')}`;
    }
  };

  const labelForAhoy = (name = '') => {
    const n = String(name || '').toLowerCase();
    if (n.includes('valuation')) return 'Viewed Valuation';
    if (n.includes('deal')) return 'Viewed Deal Room';
    if (n.includes('partner') || n.includes('search')) return 'Searched for Partners';
    if (n.includes('education')) return 'Viewed Education';
    if (n.includes('profile')) return 'Viewed Profile';
    return name || 'Activity';
  };

  const add = (items, title, at, kind) => {
    const dt = parseDate(at);
    if (!dt) return;
    items.push({ title, at: dt, kind });
  };

  const buildItems = () => {
    const items = [];
    // Direct user timestamps
    add(items, 'Ran Valuation', user.valuation_last_accessed_date, 'valuation');
    add(items, 'Updated Profile', user.profile_last_accessed_date, 'profile');
    add(items, 'Booked Consultation', user.consultation_booked_at, 'consultation');

    // Most recent choice from the Growth/Exit decision prompt
    try {
      const decisions = [];
      const dGrow = parseDate(user.growth_decision_at);
      const dExit = parseDate(user.exit_decision_at);
      const dSnooze = parseDate(user.snoozed_growth_exit_prompt_at);
      if (dGrow) decisions.push({ at: dGrow, label: 'You chose to focus on growth — keep building momentum.' });
      if (dExit) decisions.push({ at: dExit, label: 'You chose to plan your exit — we’ll guide your next steps.' });
      if (dSnooze) {
        const until = parseDate(user.growth_exit_snoozed_until);
        let label = 'You snoozed the “What’s Next” prompt for later.';
        if (user.permanently_snoozed_growth_exit_prompt) label = 'You dismissed the “What’s Next” prompt.';
        else if (until) label = `You snoozed the “What’s Next” prompt until ${until.toLocaleDateString()}.`;
        decisions.push({ at: dSnooze, label });
      }
      if (decisions.length > 0) {
        decisions.sort((a, b) => b.at.getTime() - a.at.getTime());
        const mostRecent = decisions[0];
        add(items, mostRecent.label, mostRecent.at, 'decision');
      }
    } catch (_) {}

    // Planning forms
    try { if (user?.bcp_form_datum?.updated_at) add(items, 'Worked on Continuity Plan', user.bcp_form_datum.updated_at, 'bcp'); } catch (_) {}
    try { if (user?.catastrophe_form_datum?.updated_at) add(items, 'Worked on Catastrophic Plan', user.catastrophe_form_datum.updated_at, 'catastrophe'); } catch (_) {}
    try { if (user?.succession_planning_form_datum?.updated_at) add(items, 'Worked on Succession Plan', user.succession_planning_form_datum.updated_at, 'succession_plan'); } catch (_) {}

    // Triggering events
    const evts = Array.isArray(user.triggering_events) ? user.triggering_events : [];
    evts.forEach((e) => {
      if (e && e.completed_at) add(items, labelForTriggeringEvent(e.event_type), e.completed_at, 'trigger');
    });

    // Ahoy events
    const ahoy = Array.isArray(ahoyEvents) ? ahoyEvents : [];
    ahoy.forEach((ev) => {
      const at = ev.time || ev.timestamp || ev.occurred_at;
      const name = ev.name || ev.event;
      add(items, labelForAhoy(name), at, 'ahoy');
    });

    // Deduplicate by title+date (to be safe)
    const keySet = new Set();
    const deduped = [];
    items.forEach((it) => {
      const k = `${it.title}|${it.at.toISOString()}`;
      if (!keySet.has(k)) { keySet.add(k); deduped.push(it); }
    });

    deduped.sort((a, b) => b.at.getTime() - a.at.getTime());
    return deduped.slice(0, maxItems);
  };

  const items = buildItems();

  if (!items.length) {
    return (
      <div className="text-succession-text-secondary text-sm">No recent activity yet.</div>
    );
  }

  return (
    <ol className="relative ml-2 border-l border-succession-border pl-4">
      {items.map((it, idx) => (
        <li key={`${it.title}-${idx}`} className="mb-4">
          <div className="absolute -left-2.5 w-2.5 h-2.5 bg-succession-primary rounded-full border border-white" />
          <div className="text-sm text-succession-text-primary">{it.title}</div>
          <div className="text-xs text-succession-text-secondary">
            {toRelTime(it.at)} • {it.at.toLocaleString()}
          </div>
        </li>
      ))}
    </ol>
  );
};

export default YourActivity;




